import { Moment } from 'moment';
import { Logger } from '../utils/Logger';
import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { first, isEmpty, last, orderBy } from 'lodash';
import { FormGroupUtil } from '../utils/FormGroupUtil';
import { MatTableDataSource } from '@angular/material/table';
import { FinalCertificate } from './final-certificate';
import { ConfigService } from '../service/config.service';
import { Keys, StorageService } from '../service/storage.service';
import { LenderDetailsService } from '../service/lender-details.service';
import { FinalCertificateViewModel } from './final-certificate-view-model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { InterestCertificateService } from '../service/interest-certificate.service';
import * as moment from 'moment';
import { Toaster, Type } from '../toaster/Toaster';
import { AccountService } from '../service/finzy-flex/account/account.service';

@Component({
    selector: 'app-provisionalcertificate',
    templateUrl: './interest-certificate.component.html',
    styleUrls: ['./interest-certificate.component.css'],
    providers: [InterestCertificateService]
})
export class InterestCertificateComponent implements OnInit {
    private static readonly AprilMonth = 3;

    public shouldShowFinalCertificatesTable = false;
    public shouldShowFinalCertificatesLoader = false;
    public isProvisionalCertificateFormEnabled = false;
    public shouldShowProvisionalCertificateLoader = false;
    public shouldShowProvisionalCertificatedFormLoader = false;

    public readonly financialYears;
    public readonly provisionalCertificateForm: ProvisionalCertificateForm;
    public readonly finalInterestCertificateColumnsToBeDisplayed = ['financialYear', 'downloadUrl', 'downloadStatementOfReturn'];
    public finalInterestCertificateDataSource: MatTableDataSource<FinalCertificateViewModel>;

    private readonly httpClient: HttpClient;
    private readonly configService: ConfigService;
    private readonly storageService: StorageService;
    private readonly lenderDetailsService: LenderDetailsService;
    private readonly interestCertificateService: InterestCertificateService;

    public isFINXLender: boolean = false;

    constructor(http: HttpClient, configService: ConfigService, storageService: StorageService, formBuilder: FormBuilder,
        interestCertificateService: InterestCertificateService, lenderDetailsService: LenderDetailsService,
        private accountService: AccountService) {
        this.httpClient = http;
        this.configService = configService;
        this.storageService = storageService;
        this.lenderDetailsService = lenderDetailsService;
        this.interestCertificateService = interestCertificateService;
        this.financialYears = this.generateFinancialYears().map(val => val.displayValue);

        const provisionalCertificateForm = new ProvisionalCertificateForm();
        const form = formBuilder.group({});
        form.addControl(provisionalCertificateForm.SelectedFinancialYearControlName, new FormControl('', Validators.required));
        provisionalCertificateForm.form = form;

        this.provisionalCertificateForm = provisionalCertificateForm;
        this.isFINXLender = this.storageService.getItem(Keys.FINX) === "true" && this.storageService.getItem(Keys.IS_LENDER_CREATED_AFTER_RESTRICTED_DATE) === "true";
    }

    public downloadFile(url: string): void {
        //download from mobile app
        let message = {
            action: "download_file",
            url: url
        }
        let webkit = (window as any).webkit;
        if (webkit) {
            webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(message));
        } else {
            window.location.href = url;
        }
    }

    public downloadInterestCertificateFile(financialYear: string): void {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const generatedUserId = this.storageService.getItem(Keys.GENERATED_USER_ID);
        this.accountService.downloadInterestCertificate(financialYear, lenderId)
            .then((res) => {
            const interestCertificateFile = res;
            let downloadUrl = URL.createObjectURL(interestCertificateFile);
            let downloadElem = document.createElement('a');
            downloadElem.href = downloadUrl;
            downloadElem.download = `Interest_Certificate_${generatedUserId}_${financialYear}.zip`;
            downloadElem.click();
            })
            .catch((err) => {
                console.log(err)
            })
    }

    public downloadStatementOfReturnsFile(financialYear: string): void {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const generatedUserId = this.storageService.getItem(Keys.GENERATED_USER_ID);
        this.accountService.downloadStatementOfReturns(financialYear, lenderId)
            .then((res) => {
            const interestCertificateFile = res;
            let downloadUrl = URL.createObjectURL(interestCertificateFile);
            let downloadElem = document.createElement('a');
            downloadElem.href = downloadUrl;
            downloadElem.download = `Statement_of_returns_${generatedUserId}_${financialYear}.zip`;
            downloadElem.click();
            })
            .catch((err) => {
                console.log(err)
            })
    }

    async validateAndGenerateProvisionalCertificate() {
        if (this.isProvisionalCertificateFormEnabled) {
            if (this.provisionalCertificateForm.isValid()) {
                await this.generateProvisionalCertificate(this.provisionalCertificateForm.getSelectedFinancialYear());
            } else {
                this.provisionalCertificateForm.validate();
            }
        } else {
            await this.generateProvisionalCertificate(this.currentFinancialYear().displayValue);
        }
    }

    public async generateProvisionalCertificate(financialYear: string) {
        this.showProvisionalCertificateLoader();
        try {
            const token = this.storageService.getItem(Keys.TOKEN);
            const lenderId = this.storageService.getItem(Keys.LENDER_ID);
            const profileId = this.storageService.getItem(Keys.PROFILE_ID);
            this.httpClient.post<{ source: string }>(this.configService.baseUrl() + 'interest-certificate/', {
                token: token,
                lenderId: lenderId,
                profile_id: profileId,
                financialYear: financialYear ,
                certificateType: 'provisional'
            }).subscribe(response => { Toaster.toast(`Provisional Interest Certificate for ${financialYear} will be sent to your registered email id within next 4 hours`, Type.Success) });
        } finally {
            this.hideProvisionalLoader();
        }
    }

    async ngOnInit(): Promise<void> {
        this.showSetupLoaders();
        const finalCertificates = await this.interestCertificateService.getFinalCertificates();
        const finalCertificatePerYear = this.getDistinctFinalCertificatePerYear(finalCertificates);
        await this.getAndShowFinalInterestCertificateData(finalCertificatePerYear);
        await this.setupProvisionalCertificateForm(finalCertificates);

        this.hideSetupLoaders();
    }

    public showSetupLoaders() {
        this.shouldShowFinalCertificatesLoader = this.shouldShowProvisionalCertificatedFormLoader = true;
    }

    public hideSetupLoaders() {
        this.shouldShowFinalCertificatesLoader = this.shouldShowProvisionalCertificatedFormLoader = false;
    }

    private async setupProvisionalCertificateForm(finalCertificates: FinalCertificate[]): Promise<void> {
        const previousFinancialYear = this.previousFinancialYear();
        const hasFinalCertificateForPreviousFinancialYear = this.hasFinalCertificateForPreviousFinancialYear(finalCertificates, previousFinancialYear);
        if (!hasFinalCertificateForPreviousFinancialYear) {
            this.isProvisionalCertificateFormEnabled = await this.wasLenderOnboardedBefore(previousFinancialYear.end);
        } else {
            this.isProvisionalCertificateFormEnabled = false;
        }
    }

    private hasFinalCertificateForPreviousFinancialYear(finalCertificates: FinalCertificate[], previousFinancialYear): boolean {
        return !!finalCertificates.map(certificate => certificate.financialYear).includes(previousFinancialYear.displayValue);
    }

    private async wasLenderOnboardedBefore(date: Moment): Promise<boolean> {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const { lender_info } = await this.lenderDetailsService.getLenderDetails(lenderId);
        return moment(lender_info.createdAt).isBefore(date);
    }

    private async getAndShowFinalInterestCertificateData(finalCertificates: FinalCertificate[]) {
        const token = this.storageService.getItem(Keys.TOKEN);
        const profileId = this.storageService.getItem(Keys.PROFILE_ID);
        const finalCertificateViewModels = finalCertificates.map((certificate: FinalCertificate) => {
            const finalCertificateViewModel = FinalCertificateViewModel.from(certificate, profileId, token);
            Logger.log(`Generated final certificate url: [${finalCertificateViewModel.downloadUrl}]`);
            return finalCertificateViewModel;
        });

        this.finalInterestCertificateDataSource = new MatTableDataSource<FinalCertificateViewModel>(finalCertificateViewModels);
        this.shouldShowFinalCertificatesTable = !isEmpty(finalCertificateViewModels);
    }

    private generateFinancialYears(): ({ displayValue: string; start: Moment; end: Moment })[] {
        let currentStartOfFinancialYear: number;
        const currentMonth = moment().month();
        if (currentMonth < InterestCertificateComponent.AprilMonth) {
            currentStartOfFinancialYear = moment().year() - 1;
        } else {
            currentStartOfFinancialYear = moment().year();
        }

        const previousFinancialYear = `${currentStartOfFinancialYear - 1}-${currentStartOfFinancialYear}`;
        const currentFinancialYear = `${currentStartOfFinancialYear}-${currentStartOfFinancialYear + 1}`;
        return [{
            start: moment().year(currentStartOfFinancialYear - 1).month('April').startOf('month'),
            end: moment().year(currentStartOfFinancialYear).month('March').endOf('month'),
            displayValue: previousFinancialYear
        }, {
            start: moment().year(currentStartOfFinancialYear).month('April').startOf('month'),
            end: moment().year(currentStartOfFinancialYear + 1).month('March').endOf('month'),
            displayValue: currentFinancialYear
        }];
    }

    private showProvisionalCertificateLoader() {
        this.shouldShowProvisionalCertificateLoader = true;
    }

    private hideProvisionalLoader() {
        this.shouldShowProvisionalCertificateLoader = false;
    }

    private currentFinancialYear(): { displayValue: string; start: Moment; end: Moment } {
        return last(this.generateFinancialYears());
    }

    private previousFinancialYear(): { displayValue: string; start: Moment; end: Moment } {
        return first(this.generateFinancialYears());
    }

    private getDistinctFinalCertificatePerYear(finalCertificates: FinalCertificate[]) {
        const distinctFinancialYears = Array.from(new Set(finalCertificates.map(certificate => certificate.financialYear)));
        const shortlistedCertificates = [];

        distinctFinancialYears.forEach((key: string) => {
            const certificatesForFinancialYear = finalCertificates.filter((certificate) => certificate.financialYear === key);
            shortlistedCertificates.push(orderBy(certificatesForFinancialYear, cert => cert.createdAt, 'desc')[0]);
        });
        return shortlistedCertificates;
    }
}

export class ProvisionalCertificateForm {
    form?: FormGroup;
    readonly SelectedFinancialYearControlName = 'selectedFinancialYear';

    public isValid(): boolean {
        return this.form.valid;
    }

    public isSelectedFinancialYearValid(): boolean {
        return this.form.get(this.SelectedFinancialYearControlName).invalid;
    }

    public validate(): void {
        FormGroupUtil.validateFormGroup(this.form);
    }

    public getSelectedFinancialYear(): string {
        return this.form.controls[this.SelectedFinancialYearControlName].value;
    }
}
